import React, { createContext, useCallback, useContext } from 'react'
import { useLocalStorage } from 'react-use'
import base64 from 'base-64'

interface BuilderAccordionCategory {
  design: string
  data: string
  action: string
  behavior: string
}

export type BuilderAccordionCategoryKey = keyof BuilderAccordionCategory

interface BuilderAccordionValue {
  accordions: Record<string, BuilderAccordionCategory>
  setAccordions: (tab: BuilderAccordionCategoryKey, compId: string, value: string) => void
  removeAccordions: (compId: string) => void
}

const BuilderAccordionContext = createContext<BuilderAccordionValue>({
  accordions: {},
  setAccordions: () => null,
  removeAccordions: () => null,
})

export function useBuilderAccordionContext() {
  return useContext(BuilderAccordionContext)
}

export function BuilderAccordionProvider({ children }: { children?: React.ReactNode }) {
  const [accordions, setAccordionOri] = useLocalStorage<
    Record<
      string,
      {
        design: string
        data: string
        action: string
        behavior: string
      }
    >
  >(
    'BUILDER_ACCORDION_STATE',
    {},
    {
      raw: false,
      serializer: (value: Record<string, Record<BuilderAccordionCategoryKey, string>>) => {
        return base64.encode(JSON.stringify(value))
      },
      deserializer: (value: string) => {
        return JSON.parse(base64.decode(value))
      },
    }
  )

  const setAccordions = useCallback(
    (tab: BuilderAccordionCategoryKey, compId: string, value: string) => {
      const copy = { ...accordions }
      if (!copy[compId]) {
        copy[compId] = {
          design: '',
          data: '',
          action: '',
          behavior: '',
        }
      }
      copy[compId][tab] = value
      setAccordionOri(copy)
    },
    [setAccordionOri, accordions]
  )

  const removeAccordions = useCallback(
    (compId: string) => {
      const copy = { ...accordions }
      if (copy[compId]) {
        delete copy[compId]
      }
      setAccordionOri(copy)
    },
    [accordions, setAccordionOri]
  )
  return (
    <BuilderAccordionContext.Provider
      value={{
        accordions: accordions || {},
        setAccordions,
        removeAccordions,
      }}
    >
      {children}
    </BuilderAccordionContext.Provider>
  )
}
